import React from "react";
//#region types
import {
  AutoCompleteExtraProps,
  CurrencyFieldExtraProps,
  DateFormFieldProps,
  DimensionSelectorExtraProps,
  EditableTableFieldProps,
  FileUploaderExtraProps,
  FormFieldContainerMemoizedProps,
  FormFieldDefinitionType,
  MonthRangeFormFieldProps,
  NumberFormatFieldExtraProps,
  SelectionExtraProps,
  TableListSelectionFieldProps,
} from "models/propTypes/genericFormProps";
import { FormEnums } from "models/enums";
import { FormHelper } from "utils/helpers";
//#endregion
//#region components
import usePrevious from "../usePrevious";
import useFormFieldMessage from "./hooks/useFormFieldMessage";
import useCheckRequired from "./hooks/useCheckRequired";
import FormFieldContainer from "./formFieldContainer";
import MessageBubble from "../messages/messageBubble";
import { getIn, useFormikContext } from "formik";
import { Grid } from "@mui/material";
//#endregion
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";

function FormFieldContainerMemoized<T extends Record<string, any>>(props: FormFieldContainerMemoizedProps<T>) {
  const {
    value,
    field,
    name: fieldName,
    nameForOptionalCheck,
    errors,
    touched,
    hideOptionalIndicator,
    overrideOptional,
    validationSchema,
    refValues,
  } = props;
  const [currentProps, setCurrentProps] = React.useState(props);
  const [disabled, setDisabled] = React.useState(false);
  const [readOnly, setReadOnly] = React.useState(false);
  const [isUserInput, setIsUserInput] = React.useState(false);
  const [loading, setIsLoading] = React.useState(false);
  const [enableChangeHandlers, setEnableChangeHandlers] = React.useState(false);
  const formikContext = useFormikContext<T>();
  const { setFieldTouched, setFieldValue, values, dirty, touched: formTouched } = formikContext;

  const prevValues = usePrevious(values);
  const prevValue = usePrevious(value);

  const firstUpdate = React.useRef(true);
  React.useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    setEnableChangeHandlers(true);
  });

  React.useEffect(() => {
    if (
      prevValue !== value &&
      (field.enableOnChangeAlways ||
        (enableChangeHandlers &&
          (field.enableOnChangeAlways ||
            dirty ||
            (typeof formTouched === "object" && Object.keys(formTouched).length > 0))))
    ) {
      field.onChangeHandler && field.onChangeHandler(fieldName, value, formikContext, isUserInput);
    }
    setIsUserInput(false);
  }, [value]);

  const getHideErrorBubble = (hideErrorBubbleProp?: boolean) =>
    (hideErrorBubbleProp && !field.overrideHideErrorBubble) || field.hideErrorBubble;

  const formFieldMessage = useFormFieldMessage(
    {
      value,
      values,
      errors,
      touched,
      customMessageHandler: field.customMessageHandler,
      helperText: field.helperText,
    },
    getHideErrorBubble(props.hideErrorBubble)
  );

  const messagePlacement = React.useMemo(
    () =>
      field.messagePlacement ??
      (field.widthColumnCount && !props.inline
        ? "right"
        : props.formGridLayout && !props.formGridLayout.message
          ? "top"
          : props.messagePlacement),
    []
  );

  React.useEffect(() => {
    const disabledNow = getFieldDisabled();
    if (disabledNow !== disabled) setDisabled(disabledNow);
    const readOnlyNow = getFieldReadOnly();
    if (readOnlyNow !== readOnly) setReadOnly(readOnlyNow);
    const loadingNow = getFieldLoading();
    if (loadingNow !== loading) setIsLoading(loadingNow);
  }, [values, refValues]);

  const getFieldReadOnly = () => {
    const fieldReadOnly = field.readonly;

    let isReadOnly = undefined;
    if (field.readonly !== undefined) {
      if (typeof fieldReadOnly === "boolean") {
        isReadOnly = fieldReadOnly as boolean;
      } else if (typeof fieldReadOnly === "function") {
        isReadOnly = fieldReadOnly(refValues ? refValues : values);
      } else {
      }
    }
    return !!isReadOnly;
  };

  const getFieldDisabled = () => {
    const fieldDisableProps = field.disabled ?? props.disabled;
    let isDisabled = undefined;
    if (fieldDisableProps !== undefined) {
      if (typeof fieldDisableProps === "boolean") {
        isDisabled = fieldDisableProps as boolean;
      } else if (typeof fieldDisableProps === "function") {
        isDisabled = fieldDisableProps(refValues ? refValues : values);
      } else {
        isDisabled =
          fieldDisableProps &&
          getIn(
            values,
            FormHelper.formatReferenceFieldName(fieldName, fieldDisableProps.referenceFieldName as string)
            // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if changed
          ) == fieldDisableProps.disableWhenEqualTo;
      }
    }
    return !!isDisabled;
  };

  const getFieldLoading = () => {
    return field.loading ? field.loading(values) : false;
  };

  const [required, setRequired] = React.useState<boolean>();
  const checkRequired = useCheckRequired(validationSchema);
  const getRequired = () => {
    if (!hideOptionalIndicator) {
      const fieldInfo = FormHelper.getTableRowFieldInfo(nameForOptionalCheck ?? fieldName);
      return overrideOptional === undefined
        ? checkRequired.getFieldRequired(
            String(nameForOptionalCheck ?? fieldName).replaceAll(/\[[0-9]+\]/g, ""),
            fieldInfo ? `${fieldInfo.fieldName}[${fieldInfo.rowIndex}]` : undefined
          )
        : !overrideOptional;
    }
    return true;
  };

  React.useEffect(() => {
    const currentRequired = getRequired();
    if (required !== currentRequired) setRequired(currentRequired);
  }, [values, fieldName]);

  React.useEffect(() => {
    if (
      currentProps.value !== props.value ||
      currentProps.name !== props.name ||
      currentProps.touched !== props.touched ||
      currentProps.errors !== props.errors ||
      currentProps.field.readonly !== props.field.readonly
    )
      setCurrentProps(props);
    else if (props.field.extraProps) {
      let extraProps, prevExtraProps;
      switch (props.field.type) {
        case FormEnums.FormFieldType.EDITABLETABLE:
          extraProps = props.field.extraProps as EditableTableFieldProps<T>;
          prevExtraProps = currentProps.field.extraProps as EditableTableFieldProps<T>;

          let innerFields = props.field.innerFields as FormFieldDefinitionType<T>;
          let prevInnerFields = currentProps.field.innerFields as FormFieldDefinitionType<T>;

          let autoCompleteInnerFieldsExtraProps = Object.entries(innerFields)
            .filter(([key, value]) => value?.type === FormEnums.FormFieldType.AUTOCOMPLETE)
            .flatMap(([key, value]) => value?.extraProps as AutoCompleteExtraProps<any>);

          let prevAutoCompleteInnerFieldsExtraProps = Object.entries(prevInnerFields)
            .filter(([key, value]) => value?.type === FormEnums.FormFieldType.AUTOCOMPLETE)
            .flatMap(([key, value]) => value?.extraProps as AutoCompleteExtraProps<any>);
          if (
            JSON.stringify(autoCompleteInnerFieldsExtraProps.map((i) => (Array.isArray(i.items) ? i.items : []))) !==
              JSON.stringify(
                prevAutoCompleteInnerFieldsExtraProps.map((i) => (Array.isArray(i.items) ? i.items : []))
              ) ||
            JSON.stringify(autoCompleteInnerFieldsExtraProps.map((i) => i.suggestedItems)) !==
              JSON.stringify(prevAutoCompleteInnerFieldsExtraProps.map((i) => i.suggestedItems)) ||
            JSON.stringify(extraProps.defaultRow) !== JSON.stringify(prevExtraProps.defaultRow)
          ) {
            setCurrentProps(props);
          }
          break;
        case FormEnums.FormFieldType.MULTIPLEINPUT:
          innerFields = props.field.innerFields as FormFieldDefinitionType<T>;
          prevInnerFields = currentProps.field.innerFields as FormFieldDefinitionType<T>;

          autoCompleteInnerFieldsExtraProps = Object.entries(innerFields)
            .filter(([key, value]) => value?.type === FormEnums.FormFieldType.AUTOCOMPLETE)
            .flatMap(([key, value]) => value?.extraProps as AutoCompleteExtraProps<any>);
          prevAutoCompleteInnerFieldsExtraProps = Object.entries(prevInnerFields)
            .filter(([key, value]) => value?.type === FormEnums.FormFieldType.AUTOCOMPLETE)
            .flatMap(([key, value]) => value?.extraProps as AutoCompleteExtraProps<any>);

          if (
            JSON.stringify(autoCompleteInnerFieldsExtraProps.map((i) => (Array.isArray(i.items) ? i.items : []))) !==
              JSON.stringify(
                prevAutoCompleteInnerFieldsExtraProps.map((i) => (Array.isArray(i.items) ? i.items : []))
              ) ||
            JSON.stringify(autoCompleteInnerFieldsExtraProps.map((i) => i.suggestedItems)) !==
              JSON.stringify(prevAutoCompleteInnerFieldsExtraProps.map((i) => i.suggestedItems))
          ) {
            setCurrentProps(props);
          }
          break;
        case FormEnums.FormFieldType.AUTOCOMPLETE:
        case FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE:
          extraProps = props.field.extraProps as AutoCompleteExtraProps<T>;
          prevExtraProps = currentProps.field.extraProps as AutoCompleteExtraProps<T>;
          if (
            (Array.isArray(extraProps.items) &&
              JSON.stringify(extraProps.items) !== JSON.stringify(prevExtraProps.items)) ||
            (Array.isArray(extraProps.suggestedItems) &&
              JSON.stringify(extraProps.suggestedItems) !== JSON.stringify(prevExtraProps.suggestedItems)) ||
            (extraProps.actions ?? []).map((a) => a.id).join(" ") !==
              (prevExtraProps.actions ?? []).map((a) => a.id).join(" ")
          ) {
            setCurrentProps(props);
          }
          break;
        case FormEnums.FormFieldType.CURRENCY:
          extraProps = props.field.extraProps as CurrencyFieldExtraProps<T>;
          prevExtraProps = currentProps.field.extraProps as CurrencyFieldExtraProps<T>;
          if (
            extraProps.currency !== prevExtraProps.currency ||
            (extraProps.currencyReferenceFieldName &&
              values[extraProps.currencyReferenceFieldName] !==
                (prevValues ? prevValues[extraProps.currencyReferenceFieldName] : undefined)) ||
            extraProps.isCredit !== prevExtraProps.isCredit ||
            (extraProps.isCreditReferenceFieldName &&
              values[extraProps.isCreditReferenceFieldName] !==
                (prevValues ? prevValues[extraProps.isCreditReferenceFieldName] : undefined))
          ) {
            setCurrentProps(props);
          }
          break;
        case FormEnums.FormFieldType.NUMBER:
          extraProps = props.field.extraProps as NumberFormatFieldExtraProps;
          prevExtraProps = currentProps.field.extraProps as NumberFormatFieldExtraProps;
          if (extraProps.suffix !== prevExtraProps.suffix) {
            setCurrentProps(props);
          }
          break;
        case FormEnums.FormFieldType.DATE:
          extraProps = props.field.extraProps as DateFormFieldProps<T>;
          prevExtraProps = currentProps.field.extraProps as DateFormFieldProps<T>;
          if (
            JSON.stringify(extraProps.maxDate) !== JSON.stringify(prevExtraProps.maxDate) ||
            JSON.stringify(extraProps.minDate) !== JSON.stringify(prevExtraProps.minDate) ||
            (extraProps.minDateRefFieldName &&
              JSON.stringify(values[extraProps.minDateRefFieldName]) !==
                JSON.stringify(prevValues ? prevValues[extraProps.minDateRefFieldName] : undefined)) ||
            (extraProps.maxDateRefFieldName &&
              JSON.stringify(values[extraProps.maxDateRefFieldName]) !==
                JSON.stringify(prevValues ? prevValues[extraProps.maxDateRefFieldName] : undefined))
          ) {
            setCurrentProps(props);
          }
          break;
        case FormEnums.FormFieldType.MONTHRANGE:
          extraProps = props.field.extraProps as MonthRangeFormFieldProps;
          prevExtraProps = currentProps.field.extraProps as MonthRangeFormFieldProps;
          if (JSON.stringify(extraProps) !== JSON.stringify(prevExtraProps)) setCurrentProps(props);
          break;
        case FormEnums.FormFieldType.SELECTION:
        case FormEnums.FormFieldType.CHOICE:
          extraProps = props.field.extraProps as SelectionExtraProps<T>;
          prevExtraProps = currentProps.field.extraProps as SelectionExtraProps<T>;
          if (
            JSON.stringify(extraProps.items) !== JSON.stringify(prevExtraProps.items) ||
            JSON.stringify(extraProps.disabledItems) !== JSON.stringify(prevExtraProps.disabledItems)
          )
            setCurrentProps(props);
          break;
        case FormEnums.FormFieldType.DIMENSIONSELECTOR:
          extraProps = props.field.extraProps as DimensionSelectorExtraProps;
          prevExtraProps = currentProps.field.extraProps as DimensionSelectorExtraProps;
          if (extraProps.amount !== prevExtraProps.amount || extraProps.currency !== prevExtraProps.currency)
            setCurrentProps(props);
          break;
        case FormEnums.FormFieldType.TABLELISTSELECTION:
          extraProps = props.field.extraProps as TableListSelectionFieldProps<T>;
          prevExtraProps = currentProps.field.extraProps as TableListSelectionFieldProps<T>;
          if (
            extraProps.search !== prevExtraProps.search ||
            extraProps.getSuggestions !== prevExtraProps.getSuggestions
          )
            setCurrentProps(props);
          break;
        case FormEnums.FormFieldType.FILEUPLOADER:
          extraProps = props.field.extraProps as FileUploaderExtraProps;
          prevExtraProps = currentProps.field.extraProps as FileUploaderExtraProps;
          if (JSON.stringify(extraProps) !== JSON.stringify(prevExtraProps)) setCurrentProps(props);
          break;
      }
    }
  }, [props, values]);

  const onChangeFromUIHandler = (updatedValue: any) => {
    setIsUserInput(true);
  };

  const component = React.useMemo(
    () => (
      <FormFieldContainer
        fieldRef={currentProps.fieldRef}
        name={currentProps.name}
        field={currentProps.field}
        hideErrorBubble={getHideErrorBubble(currentProps.hideErrorBubble)}
        hideSuccess={currentProps.hideSuccess}
        messagePlacement={currentProps.messagePlacement}
        invertedDisplayType={currentProps.invertedDisplayType}
        inline={currentProps.inline}
        enableControlLables={currentProps.enableControlLables}
        contentWidth={currentProps.contentWidth}
        formGridLayout={currentProps.formGridLayout}
        layout={currentProps.layout}
        validationSchema={currentProps.validationSchema}
        overrideOptional={currentProps.overrideOptional}
        hideOptionalIndicator={currentProps.hideOptionalIndicator}
        focusField={currentProps.focusField}
        value={currentProps.value}
        errors={currentProps.errors}
        touched={currentProps.touched}
        setFieldTouched={setFieldTouched}
        setFieldValue={setFieldValue}
        disabled={disabled}
        optional={!required}
        readonly={readOnly}
        onChangeFromUIHandler={onChangeFromUIHandler}
        loading={loading}
      />
    ),
    [currentProps, disabled, required, readOnly, loading]
  );

  return required !== undefined ? (
    <Grid
      container
      item
      sm={field.widthColumnCount}
      className={clsx(field.rightAlign ? cssExports.formFieldRightAlign : "", field.classes?.formField)}
      data-testid={`${props.name}FormField`}
    >
      {field.customContentBefore ? field.customContentBefore(values) : <></>}
      {getHideErrorBubble(props.hideErrorBubble) ? (
        component
      ) : (
        <Grid
          container
          direction={messagePlacement === "right" ? "row" : "column-reverse"}
          className={
            messagePlacement === "right"
              ? cssExports.formFieldMessageAttachElemRight
              : cssExports.formFieldMessageAttachElemTop
          }
        >
          <Grid container item xs>
            {component}
          </Grid>
          <MessageBubble
            message={formFieldMessage?.message}
            type={formFieldMessage?.type}
            placement={messagePlacement}
          ></MessageBubble>
        </Grid>
      )}
      {field.customContentAfter ? field.customContentAfter(values) : <></>}
    </Grid>
  ) : (
    <></>
  );
}

export default FormFieldContainerMemoized;
